let colourMediaRequestId = 0;

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        // URL ?variant= sold-out size must not leave that id in the form while sizes are unchecked.
        this.sanitizeIncompleteSizeSelection();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      /**
       * If no size is selected (or a checked size is unavailable), never keep an
       * unavailable variant id in the product form. Does not auto-select a size.
       * Sold-out beside price must follow selected-colour availability, not a
       * default unavailable size (e.g. Carrot Red / S).
       */
      sanitizeIncompleteSizeSelection() {
        window.ProductSizeGuard?.clearUnavailableCheckedSizes(this);
        const missingSizes = window.ProductSizeGuard?.getMissing(this) || [];
        if (!missingSizes.length) return;

        // No size selected → form must not hold a cart-ready id (especially sold-out size URLs).
        this.updateVariantInputs('', { silent: true });
        this.applyIncompleteSizeAvailability();
      }

      getProductVariants() {
        const script =
          this.querySelector(`#ProductVariants-${this.dataset.section}`) ||
          this.querySelector('[id^="ProductVariants-"]');
        if (!script) return [];
        try {
          const parsed = JSON.parse(script.textContent);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return [];
        }
      }

      getOptionIndexForInput(input) {
        const picker = input?.closest('variant-selects, variant-radios');
        if (!picker || !input) return -1;
        const groups = Array.from(picker.children).filter(
          (el) => el.matches && el.matches('fieldset.product-form__input, .product-form__input')
        );
        const group = input.closest('fieldset.product-form__input, .product-form__input');
        return groups.indexOf(group);
      }

      /**
       * With size unselected, product-level availability = any variant that matches
       * the currently selected non-size options (usually colour) and is available.
       */
      hasAvailableVariantForPartialSelection() {
        const variants = this.getProductVariants();
        if (!variants.length) return true;

        const selected = [];
        this.querySelectorAll(
          'variant-selects input[type="radio"]:checked, variant-radios input[type="radio"]:checked'
        ).forEach((input) => {
          if (window.ProductSizeGuard?.isSizeInput(input)) return;
          const index = this.getOptionIndexForInput(input);
          if (index < 0 || !input.value) return;
          selected.push({ index, value: input.value });
        });

        if (!selected.length) {
          return variants.some((variant) => variant.available);
        }

        return variants.some((variant) => {
          if (!variant.available) return false;
          const options = variant.options || [variant.option1, variant.option2, variant.option3];
          return selected.every(({ index, value }) => options[index] === value);
        });
      }

      /** First variant (available preferred) matching the checked non-size options, for the URL only. */
      getVariantIdForPartialSelection() {
        const variants = this.getProductVariants();
        const selected = [];
        this.querySelectorAll(
          'variant-selects input[type="radio"]:checked, variant-radios input[type="radio"]:checked'
        ).forEach((input) => {
          if (window.ProductSizeGuard?.isSizeInput(input)) return;
          const index = this.getOptionIndexForInput(input);
          if (index < 0 || !input.value) return;
          selected.push({ index, value: input.value });
        });
        if (!selected.length) return undefined;

        const matches = variants.filter((variant) => {
          const options = variant.options || [variant.option1, variant.option2, variant.option3];
          return selected.every(({ index, value }) => options[index] === value);
        });
        return (matches.find((variant) => variant.available) || matches[0])?.id;
      }

      setPriceSoldOut(soldOut) {
        const priceWrap = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceWrap) return;
        priceWrap.classList.remove('hidden');
        const priceEl = priceWrap.querySelector('.price');
        if (!priceEl) return;
        priceEl.classList.remove('hidden');
        priceEl.classList.toggle('price--sold-out', !!soldOut);
      }

      /**
       * Colour selected, size not: Sold out only if every size for that colour is unavailable.
       * Does not auto-select a size.
       */
      applyIncompleteSizeAvailability() {
        const colourAvailable = this.hasAvailableVariantForPartialSelection();
        this.setPriceSoldOut(!colourAvailable);
        if (colourAvailable) {
          this.productForm?.toggleSubmitButton(false);
        } else {
          this.productForm?.toggleSubmitButton(true, window.variantStrings.soldOut);
        }
        return colourAvailable;
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
  if (!this.contains(event.target)) return;

  this.resetProductFormState();

  const productUrl =
    target.dataset.productUrl ||
    this.pendingRequestUrl ||
    this.dataset.url;

  this.pendingRequestUrl = productUrl;

  const shouldSwapProduct = this.dataset.url !== productUrl;
  const shouldFetchFullPage =
    this.dataset.updateUrl === 'true' && shouldSwapProduct;

  /*
   * Hazel Wolf fix:
   * When a colour swatch opens another linked product,
   * do not carry the previously selected size into that product.
   */
  const targetName = (
    target.name ||
    target.getAttribute('name') ||
    ''
  ).toLowerCase();

  const legendText = (
    target.closest('fieldset, .product-form__input')?.querySelector('legend')?.textContent ||
    ''
  ).toLowerCase();

  const isColourSelection =
    targetName.includes('color') ||
    targetName.includes('colour') ||
    legendText.includes('color') ||
    legendText.includes('colour');

  if (isColourSelection) {
    // Module-level so IDs survive linked-product <product-info> swaps.
    colourMediaRequestId += 1;
    // Immediately clear old media and show a stable neutral placeholder.
    this.enterColourMediaLoading();
    // Clear size BEFORE the option-values request is built/used.
    window.ProductSizeGuard?.uncheckSizeRadios(this);
  }

  let requestOptionValues = selectedOptionValues;

  if (isColourSelection) {
    // Same product or linked product: never carry a previous size into the colour request.
    // Carrying Size S into Carrot Red/Pink was selecting the sold-out Red/S variant.
    requestOptionValues = [target.dataset.optionValueId].filter(Boolean);
  }

  const isSizeSelection =
    window.ProductSizeGuard?.isSizeInput(target) || targetName.includes('size');

  if (isColourSelection) {
    this.lockColourScrollPosition();
  }

  this.renderProductInfo({
    requestUrl: this.buildRequestUrlWithParams(
      productUrl,
      requestOptionValues,
      shouldFetchFullPage
    ),
    targetId: target.id,
    // Colour swaps must not focus/scroll the page.
    skipFocus: isSizeSelection || isColourSelection,
    callback: shouldSwapProduct
      ? this.handleSwapProduct(
          productUrl,
          shouldFetchFullPage,
          isColourSelection
        )
      : this.handleUpdateProductInfo(productUrl, isSizeSelection, isColourSelection),
  });
}

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage, isColourSelection = false) {
        return async (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const nextProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(nextProductInfo);
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            this.exitColourMediaLoading();
            this.signalColourMediaReady();
            return;
          }

          if (isColourSelection) {
            await this.swapProductPreservingMedia(nextProductInfo);
            return;
          }

          HTMLUpdateUtility.viewTransition(
            this,
            nextProductInfo,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
          this.exitColourMediaLoading();
          this.signalColourMediaReady();
        };
      }

      renderProductInfo({ requestUrl, targetId, callback, skipFocus = false }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const requestIdAtFetch = colourMediaRequestId;

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then(async (responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            await callback(html);
          })
          .then(() => {
            // Focusing the radio on mobile after AJAX replace causes scroll/flicker.
            if (skipFocus) {
              this.restoreColourScrollPosition();
              return;
            }
            // Only focus if this is still the latest colour request (or non-colour).
            if (requestIdAtFetch !== colourMediaRequestId) return;
            document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
          })
          .catch((error) => {
          if (error.name === 'AbortError') {
            // Newer colour request superseded this one — keep placeholder until latest finishes.
            return;
          }
          console.error(error);
          this.exitColourMediaLoading();
          this.signalColourMediaReady();
          this.restoreColourScrollPosition();
        });
      }

      signalColourMediaReady() {
        document.dispatchEvent(new CustomEvent('product-color-media:ready'));
      }

      getLiveMediaWrapper() {
        const live =
          Array.from(document.querySelectorAll('product-info[id^="MainProduct"]')).find(
            (node) => node.style.display !== 'none' && node.getAttribute('hidden') == null
          ) || this;
        return live.querySelector?.('.product__media-wrapper') || document.querySelector('.product__media-wrapper');
      }

      /**
       * Colour click: hide old media immediately, lock container size, show
       * neutral placeholder. No old image remains visible.
       */
      lockColourScrollPosition() {
        this._colourScrollY = window.scrollY || window.pageYOffset || 0;
        document.documentElement.classList.add('colour-media-scroll-lock');
      }

      restoreColourScrollPosition() {
        const y = this._colourScrollY;
        document.documentElement.classList.remove('colour-media-scroll-lock');
        if (typeof y !== 'number') return;
        const apply = () => window.scrollTo(0, y);
        apply();
        requestAnimationFrame(() => {
          apply();
          requestAnimationFrame(apply);
        });
      }

      lockMobileMediaFrame(wrapper = this.getLiveMediaWrapper()) {
        if (!wrapper || !window.matchMedia('(max-width: 749px)').matches) return;
        if (wrapper.classList.contains('hw-media-frame-locked') && wrapper.style.getPropertyValue('--hw-mobile-media-frame-height')) {
          return;
        }
        const media =
          wrapper.querySelector('.product__media-item:not(.hide-media) .media') ||
          wrapper.querySelector('.product__media-item:not(.hide-media) .product__media') ||
          wrapper.querySelector('.product__media-item:not(.hide-media)');
        if (!media) return;
        const height = Math.round(media.getBoundingClientRect().height || media.offsetHeight || wrapper.offsetHeight);
        const width = Math.round(media.getBoundingClientRect().width || media.offsetWidth || wrapper.offsetWidth);
        if (height < 40 || width < 40) return;
        const pct = ((height / width) * 100).toFixed(3);
        wrapper.style.setProperty('--hw-mobile-media-frame-height', `${height}px`);
        wrapper.style.setProperty('--hw-mobile-media-ratio-pct', `${pct}%`);
        wrapper.style.minHeight = `${height}px`;
        wrapper.classList.add('hw-media-frame-locked');
        document.documentElement.style.setProperty('--hw-mobile-media-ratio-pct', `${pct}%`);
      }

      enterColourMediaLoading(wrapper = this.getLiveMediaWrapper()) {
        if (!wrapper) return;
        this.lockColourScrollPosition();
        this.lockMobileMediaFrame(wrapper);
        // Keep RL-style swatch spinner visible even if the picker remounts.
        const colourInput =
          this.querySelector('fieldset.product-form__input--swatch input:checked') ||
          document.querySelector('fieldset.product-form__input--swatch input:checked');
        window.setColourSwatchLoading?.(colourInput);
        const height =
          parseFloat(wrapper.style.getPropertyValue('--hw-mobile-media-frame-height')) ||
          wrapper.offsetHeight;
        if (height > 0) {
          wrapper.style.minHeight = `${height}px`;
          wrapper.style.setProperty('--variant-loading-height', `${height}px`);
        }

        // Mobile: keep current gallery visible (no blank pin / no dim).
        // Desktop: keep the existing neutral placeholder behaviour.
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        if (isMobile) {
          wrapper.classList.remove('variant-loading', 'colour-media-preparing', 'has-colour-media-pin');
          wrapper.querySelectorAll('.hw-colour-media-pin').forEach((el) => el.remove());
          const list = wrapper.querySelector('.product__media-list');
          if (list) {
            list.style.scrollSnapType = 'none';
            list.scrollLeft = 0;
          }
          return;
        }

        wrapper.classList.add('variant-loading');
        wrapper.classList.remove('colour-media-preparing');
        const list = wrapper.querySelector('.product__media-list');
        if (list) {
          list.style.scrollSnapType = 'none';
          list.scrollLeft = 0;
        }
      }

      exitColourMediaLoading(root = null) {
        const wrappers = root
          ? [root.querySelector?.('.product__media-wrapper')].filter(Boolean)
          : Array.from(
              document.querySelectorAll(
                '.product__media-wrapper.variant-loading, .product__media-wrapper.colour-media-preparing'
              )
            );

        window.clearHwColourMediaFrame?.();

        if (!wrappers.length) {
          const fallback = this.getLiveMediaWrapper();
          if (fallback) void this.revealColourGalleryLeadOnly(fallback);
          return;
        }

        wrappers.forEach((wrapper) => {
          void this.revealColourGalleryLeadOnly(wrapper);
        });
      }

      /**
       * Nuclear fix for "image 2 then image 1" flash:
       * 1) Pull every non-lead slide OUT of the DOM
       * 2) Show only image 1 (scrollLeft forced to 0, snap off)
       * 3) After image 1 has real dimensions, append the rest
       */
      async revealColourGalleryLeadOnly(wrapper) {
        if (!wrapper) return;

        const list = wrapper.querySelector('.product__media-list');
        const viewer = wrapper.querySelector('slider-component, [id^="GalleryViewer"]');
        if (!list) {
          wrapper.classList.remove('variant-loading', 'colour-media-settling', 'colour-media-preparing');
          this.restoreColourScrollPosition();
          return;
        }

        const items = Array.from(list.querySelectorAll(':scope > .product__media-item'));
        if (!items.length) {
          wrapper.classList.remove('variant-loading', 'colour-media-settling', 'colour-media-preparing');
          this.restoreColourScrollPosition();
          return;
        }

        // Image 1 = first non-hidden colour slide, else first slide.
        let lead =
          items.find((item) => item.classList.contains('media-gallery-lead') && !item.classList.contains('hide-media')) ||
          items.find((item) => !item.classList.contains('hide-media')) ||
          items[0];

        // Mobile: keep slides in the DOM. Removing nodes above the fold
        // makes iOS/Android jump the page to the logo (looks like a reload).
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const rest = [];

        // Mobile: do not defer/hide sibling slides during settle — the pin covers
        // the swap, and hiding siblings still causes a blank flash when the pin lifts.
        if (isMobile) {
          items.forEach((item) => {
            if (item === lead) {
              item.classList.remove('hide-media', 'colour-media-deferred');
              item.classList.add('is-active', 'media-gallery-lead');
              return;
            }
            rest.push(item);
            item.classList.remove('is-active', 'media-gallery-lead', 'colour-media-deferred');
          });
          if (list.firstElementChild !== lead) {
            list.insertBefore(lead, list.firstElementChild);
          }
        } else {
          items.forEach((item) => {
            if (item === lead) return;
            rest.push(item);
            item.remove();
          });
          list.appendChild(lead);
          lead.classList.remove('hide-media', 'colour-media-deferred');
          lead.classList.add('is-active', 'media-gallery-lead');
        }

        list.style.scrollSnapType = 'none';
        list.style.overflowX = 'hidden';
        list.scrollLeft = 0;
        if (viewer && 'scrollLeft' in viewer) viewer.scrollLeft = 0;

        if (!isMobile) {
          wrapper.classList.add('colour-media-settling');
        }
        wrapper.classList.remove('variant-loading', 'colour-media-preparing');
        this.restoreColourScrollPosition();

        const img = lead.querySelector('img');
        if (img) {
          img.loading = 'eager';
          img.removeAttribute('loading');
          img.setAttribute('fetchpriority', 'high');
        }

        await this.preloadMediaElement(lead);

        // Mobile: new lead is already preloaded before reveal — avoid long settle waits
        // that leave the gallery feeling blank/heavy after a colour tap.
        if (isMobile) {
          list.scrollLeft = 0;
          rest.forEach((item) => {
            item.classList.remove('is-active', 'media-gallery-lead', 'colour-media-deferred');
          });
          list.scrollLeft = 0;
          const sliderComponentMobile = wrapper.querySelector('slider-component');
          if (sliderComponentMobile) {
            try {
              sliderComponentMobile.resetPages?.();
              sliderComponentMobile.currentPage = 1;
              if (sliderComponentMobile.slider) sliderComponentMobile.slider.scrollLeft = 0;
            } catch (e) {
              /* ignore */
            }
          }
          requestAnimationFrame(() => {
            list.scrollLeft = 0;
            list.style.removeProperty('scroll-snap-type');
            list.style.removeProperty('overflow-x');
            wrapper.classList.remove('colour-media-settling', 'colour-media-preparing');
            this.lockMobileMediaFrame(wrapper);
            const locked = wrapper.style.getPropertyValue('--hw-mobile-media-frame-height');
            if (locked) {
              wrapper.style.minHeight = locked;
              wrapper.style.setProperty('--variant-loading-height', locked);
            }
            this.restoreColourScrollPosition();
            document.dispatchEvent(new CustomEvent('product-color-media:settled'));
          });
          return;
        }

        if (img && !(img.complete && img.naturalWidth > 0)) {
          await new Promise((resolve) => {
            const done = () => {
              img.removeEventListener('load', done);
              img.removeEventListener('error', done);
              resolve();
            };
            img.addEventListener('load', done);
            img.addEventListener('error', done);
            setTimeout(done, 2500);
          });
        }

        // Ensure image 1 has painted height while it is still the only slide.
        await new Promise((resolve) => {
          let frames = 0;
          const tick = () => {
            list.scrollLeft = 0;
            this.restoreColourScrollPosition();
            frames += 1;
            if ((lead.offsetHeight > 40 && img?.naturalWidth > 0) || frames > 45) {
              resolve();
              return;
            }
            requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        });

        list.scrollLeft = 0;

        // Reveal remaining slides AFTER image 1 is on screen alone.
        rest.forEach((item) => {
          item.classList.remove('is-active', 'media-gallery-lead', 'colour-media-deferred');
          if (!isMobile) list.appendChild(item);
        });

        list.scrollLeft = 0;
        this.restoreColourScrollPosition();

        const sliderComponent = wrapper.querySelector('slider-component');
        if (sliderComponent) {
          try {
            sliderComponent.resetPages?.();
            sliderComponent.currentPage = 1;
            if (sliderComponent.slider) sliderComponent.slider.scrollLeft = 0;
          } catch (e) {
            /* ignore */
          }
        }

        // Keep snap off for one more frame so mandatory snap cannot jump to slide 2.
        requestAnimationFrame(() => {
          list.scrollLeft = 0;
          this.restoreColourScrollPosition();
          requestAnimationFrame(() => {
            list.scrollLeft = 0;
            list.style.removeProperty('scroll-snap-type');
            list.style.removeProperty('overflow-x');
            wrapper.classList.remove('colour-media-settling');
            // Mobile: NEVER release the locked frame height — layout must stay stable.
            if (!isMobile) {
              wrapper.style.removeProperty('min-height');
              wrapper.style.removeProperty('--variant-loading-height');
            } else {
              this.lockMobileMediaFrame(wrapper);
              const locked = wrapper.style.getPropertyValue('--hw-mobile-media-frame-height');
              if (locked) {
                wrapper.style.minHeight = locked;
                wrapper.style.setProperty('--variant-loading-height', locked);
              }
            }
            this.restoreColourScrollPosition();
            document.dispatchEvent(new CustomEvent('product-color-media:settled'));
          });
        });
      }

      resolveColourLeadMediaId(root, featuredMediaId) {
        const scope = root || this;
        const sectionId = scope.dataset?.section || this.dataset.section;

        // Always prefer the first slide already in the gallery list (true "1st image").
        const firstInList = scope.querySelector(
          '.product__media-list > .product__media-item:not(.hide-media)'
        ) || scope.querySelector('.product__media-list > .product__media-item');

        if (firstInList?.dataset?.mediaId) {
          return firstInList.dataset.mediaId;
        }

        if (featuredMediaId) {
          const id = String(featuredMediaId);
          return id.includes(String(sectionId)) ? id : `${sectionId}-${id}`;
        }

        return null;
      }

      /**
       * Preload/decode ONLY the first media item for the selected colour.
       * Prefers decoding the real DOM <img>/<video> that will be shown.
       */
      preloadMediaElement(mediaRoot) {
        if (!mediaRoot) return Promise.resolve();

        const img = mediaRoot.querySelector('img');
        if (img) {
          img.loading = 'eager';
          img.removeAttribute('loading');
          img.setAttribute('fetchpriority', 'high');

          if (img.complete && img.naturalWidth > 0) {
            return typeof img.decode === 'function' ? img.decode().catch(() => {}) : Promise.resolve();
          }

          const waitForImg = () => {
            if (typeof img.decode === 'function') {
              return img.decode().catch(() => {});
            }
            return new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            });
          };

          // Also warm HTTP cache with a matching responsive preloader.
          const preloader = new Image();
          if (img.sizes) preloader.sizes = img.sizes;
          if (img.srcset) preloader.srcset = img.srcset;
          preloader.src = img.currentSrc || img.src;

          return Promise.all([
            waitForImg(),
            typeof preloader.decode === 'function'
              ? preloader.decode().catch(() => {})
              : new Promise((resolve) => {
                  preloader.onload = resolve;
                  preloader.onerror = resolve;
                }),
          ]).then(() => {});
        }

        const video = mediaRoot.querySelector('video');
        if (video) {
          return new Promise((resolve) => {
            if (video.readyState >= 2) {
              resolve();
              return;
            }
            const done = () => {
              video.removeEventListener('loadeddata', done);
              video.removeEventListener('canplay', done);
              video.removeEventListener('error', done);
              resolve();
            };
            video.addEventListener('loadeddata', done);
            video.addEventListener('canplay', done);
            video.addEventListener('error', done);
            try {
              video.load();
            } catch (e) {
              /* ignore */
            }
            setTimeout(done, 1500);
          });
        }

        return Promise.resolve();
      }

      preloadFirstGalleryMedia(root) {
        if (!root) return Promise.resolve();
        const item =
          root.querySelector('.product__media-item.is-active') ||
          root.querySelector('media-gallery .product__media-item') ||
          root.querySelector('.product__media-item');
        return this.preloadMediaElement(item);
      }

      /**
       * Force gallery IMAGE 1 (first of selected colour) as lead before reveal.
       * Do not use a secondary featured shot as the lead — that caused
       * "image 2 flashes, then jumps to image 1".
       */
      activateFeaturedMediaBeforeReveal(root, mediaId) {
        const scope = root || this;
        const gallery = scope.querySelector('media-gallery');
        if (!gallery) return null;

        const list =
          gallery.querySelector('.product__media-list') || gallery.querySelector('[id^="GalleryViewer"] ul');
        if (!list) return null;

        const allItems = Array.from(list.querySelectorAll(':scope > .product__media-item'));
        const visible = allItems.filter((item) => !item.classList.contains('hide-media'));

        // Prefer caller id only if it is already among the visible colour slides;
        // otherwise always take the first visible slide (true gallery image 1).
        let featured = null;
        if (mediaId) {
          const id = String(mediaId);
          featured = visible.find((item) => {
            const raw = String(item.dataset.mediaId || '');
            return raw === id || raw.endsWith('-' + id.split('-').pop()) || raw.endsWith(id);
          });
        }
        if (!featured) featured = visible[0] || allItems[0];
        if (!featured) return null;

        const leadId = featured.dataset.mediaId;
        window.setColourMediaLeadId?.(leadId);

        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        if (isMobile) {
          // Minimal DOM moves on mobile — full reshuffles jump the page to the logo.
          if (list.firstElementChild !== featured) {
            list.insertBefore(featured, list.firstElementChild);
          }
        } else {
          const visibleOthers = visible.filter((item) => item !== featured);
          const hiddenItems = allItems.filter((item) => item.classList.contains('hide-media'));
          [featured, ...visibleOthers, ...hiddenItems].forEach((item) => list.appendChild(item));
        }

        allItems.forEach((item) => {
          item.classList.remove('is-active', 'media-gallery-lead', 'colour-media-deferred');
        });
        featured.classList.remove('hide-media');
        featured.classList.add('is-active', 'media-gallery-lead');

        list.scrollLeft = 0;

        const featuredImg = featured.querySelector('img');
        if (featuredImg) {
          featuredImg.loading = 'eager';
          featuredImg.removeAttribute('loading');
          featuredImg.setAttribute('fetchpriority', 'high');
        }

        gallery.dataset.colourMediaSwap = 'true';
        if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(leadId, false);
        }
        featured.classList.add('is-active', 'media-gallery-lead');
        delete gallery.dataset.colourMediaSwap;
        list.scrollLeft = 0;
        this.restoreColourScrollPosition();

        return featured;
      }

      /**
       * Linked-colour product swap:
       * placeholder → prepare featured media off-screen → reveal once.
       * No crossfade/slide; no post-reveal reorder flicker.
       */
      async swapProductPreservingMedia(newContent) {
        const requestId = colourMediaRequestId;

        this.preProcessHtmlCallbacks?.forEach((callback) => callback(newContent));

        const newNodeWrapper = document.createElement('div');
        HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
        const newNode = newNodeWrapper.firstChild;
        if (!newNode) {
          this.exitColourMediaLoading();
          this.signalColourMediaReady();
          return;
        }

        const variant = this.getSelectedVariant(newNode);
        const featuredMediaId = variant?.featured_media?.id;
        const sectionId = newNode.dataset?.section || this.dataset.section;
        const mediaId = featuredMediaId ? `${sectionId}-${featuredMediaId}` : null;

        await this.preloadFirstGalleryMedia(newNode);
        if (requestId !== colourMediaRequestId) return;

        const outgoing =
          Array.from(document.querySelectorAll('product-info[id^="MainProduct"]')).find(
            (node) => node.style.display !== 'none' && node.getAttribute('hidden') == null
          ) || this;

        if (!outgoing?.isConnected || requestId !== colourMediaRequestId) return;

        document.querySelectorAll('product-info[id^="MainProduct"]').forEach((node) => {
          if (node !== outgoing) node.remove();
        });

        const oldMediaWrap = outgoing.querySelector('.product__media-wrapper');
        const lockedHeight =
          oldMediaWrap?.style.minHeight ||
          (oldMediaWrap?.offsetHeight ? `${oldMediaWrap.offsetHeight}px` : '');

        const uniqueKey = Date.now();
        outgoing.querySelectorAll('[id], [form]').forEach((element) => {
          element.id && (element.id = `${element.id}-${uniqueKey}`);
          element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
        });

        const newMediaWrap = newNode.querySelector('.product__media-wrapper');
        if (newMediaWrap) {
          if (lockedHeight) {
            newMediaWrap.style.minHeight = lockedHeight;
            newMediaWrap.style.setProperty('--variant-loading-height', lockedHeight);
          }
          newMediaWrap.classList.add('variant-loading');
        }

        outgoing.parentNode.insertBefore(newNode, outgoing);
        outgoing.style.display = 'none';

        this.postProcessHtmlCallbacks?.forEach((callback) => callback(newNode));

        // Filter first, then lock gallery image 1 (not a secondary featured shot).
        window.initProductColorMedia?.();
        document.dispatchEvent(new CustomEvent('product-color-media:ready'));

        const firstVisible = newNode.querySelector(
          '.product__media-list > .product__media-item:not(.hide-media)'
        ) || newNode.querySelector('.product__media-list > .product__media-item');
        const leadId = firstVisible?.dataset?.mediaId || mediaId;
        window.setColourMediaLeadId?.(leadId);

        let liveFeatured = this.activateFeaturedMediaBeforeReveal(newNode, leadId);
        await this.preloadMediaElement(liveFeatured);

        if (requestId !== colourMediaRequestId) {
          outgoing.remove();
          return;
        }

        liveFeatured = this.activateFeaturedMediaBeforeReveal(newNode, leadId) || liveFeatured;

        await this.revealColourGalleryLeadOnly(newMediaWrap);
        outgoing.remove();
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }
updateOptionValues(html, { skipPickerSwap = false } = {}) {
  const variantSelects = html.querySelector('variant-selects');
  if (!variantSelects || !this.variantSelectors) return;

  // Size pills must not be replaced. Server HTML always renders sizes unchecked,
  // so a swap flashes a second empty size row on mobile.
  if (skipPickerSwap) {
    this.syncVariantPickerInPlace(variantSelects);
    return;
  }

  const oldInputs = Array.from(this.variantSelectors.querySelectorAll('input[type="radio"]'));
  const checkedSize = oldInputs.find(
    (input) => input.checked && (input.name || '').toLowerCase().includes('size')
  );
  const checkedSizeValue = checkedSize?.value;

  HTMLUpdateUtility.viewTransition(
    this.variantSelectors,
    variantSelects,
    this.preProcessHtmlCallbacks,
    this.postProcessHtmlCallbacks
  );

  // Server HTML leaves sizes unchecked — restore the user's size only if still available.
  // Never restore a disabled/unavailable size, and never auto-select a different size.
  if (checkedSizeValue) {
    const newInputs = Array.from(this.variantSelectors?.querySelectorAll('input[type="radio"]') || []);
    const match = newInputs.find(
      (input) => (input.name || '').toLowerCase().includes('size') && input.value === checkedSizeValue
    );
    const unavailable =
      !match ||
      match.classList.contains('disabled') ||
      match.classList.contains('visually-disabled') ||
      match.hasAttribute('disabled');
    if (match && !unavailable) {
      match.checked = true;
      match.setAttribute('checked', 'checked');
    }
  }

  window.ProductSizeGuard?.clearUnavailableCheckedSizes(this.variantSelectors);
}

syncVariantPickerInPlace(fetchedPicker) {
  const livePicker = this.variantSelectors;
  if (!livePicker || !fetchedPicker) return;

  const copyScript = (selector) => {
    const from = fetchedPicker.querySelector(selector);
    const to = livePicker.querySelector(selector);
    if (from && to) to.innerHTML = from.innerHTML;
  };

  copyScript('[data-selected-variant]');
  copyScript('[id^="ProductVariants-"]');

  const liveInputs = Array.from(livePicker.querySelectorAll('input[type="radio"]'));
  fetchedPicker.querySelectorAll('input[type="radio"]').forEach((fetchedInput) => {
    const liveInput = liveInputs.find(
      (input) =>
        input.dataset.optionValueId === fetchedInput.dataset.optionValueId ||
        (input.name === fetchedInput.name && input.value === fetchedInput.value)
    );
    if (!liveInput) return;

    liveInput.classList.toggle('disabled', fetchedInput.classList.contains('disabled'));
    liveInput.classList.toggle('visually-disabled', fetchedInput.classList.contains('visually-disabled'));
    if (fetchedInput.hasAttribute('disabled')) {
      liveInput.setAttribute('disabled', '');
    } else {
      liveInput.removeAttribute('disabled');
    }
  });

  // After availability sync: if a checked size is now unavailable, clear it.
  // Do NOT auto-select another available size.
  window.ProductSizeGuard?.clearUnavailableCheckedSizes(livePicker);

  // Keep written colour name in sync with the checked swatch (never leave stale "Black").
  const checkedColour =
    livePicker.querySelector('.product-form__input--swatch input[type="radio"]:checked') ||
    liveInputs.find((input) => {
      const name = (input.name || '').toLowerCase();
      return input.checked && (name.includes('color') || name.includes('colour'));
    });
  if (checkedColour?.value) {
    livePicker.querySelectorAll('.selected-color-name').forEach((el) => {
      el.textContent = checkedColour.value;
    });
  }
}

      handleUpdateProductInfo(productUrl, isSizeSelection = false, isColourSelection = false) {
        return async (html) => {
          const variant = this.getSelectedVariant(html);
          const requestId = colourMediaRequestId;

          this.pickupAvailability?.update(variant);
          // Colour must not remount variant-selects — that reloads the product block on mobile.
          this.updateOptionValues(html, { skipPickerSwap: isSizeSelection || isColourSelection });

          // After availability sync, drop any checked unavailable size (never auto-pick another).
          window.ProductSizeGuard?.clearUnavailableCheckedSizes(this);
          const missingSizes = window.ProductSizeGuard?.getMissing(this) || [];
          const needsSize = missingSizes.length > 0;

          if (variant) {
            if (needsSize) {
              // No size chosen: never leave a cart-ready variant id (sold-out or a default size).
              // Media/price still update from the colour fetch; Add to Bag requires a size.
              // The URL keeps the colour's variant (even if that size is sold out) so a refresh restores the colour.
              this.updateURL(productUrl, variant.id);
              this.updateVariantInputs('', { silent: true });
            } else {
              this.updateURL(productUrl, variant.id);
              this.updateVariantInputs(variant.id);
            }
          } else if (!needsSize) {
            this.updateURL(productUrl);
            this.updateVariantInputs('');
            this.setUnavailable();
            this.exitColourMediaLoading();
            this.signalColourMediaReady();
            return;
          }

          if (!variant) {
            // Colour chosen, size still required — not a dead product; wait for size.
            if (needsSize) {
              this.updateURL(productUrl, this.getVariantIdForPartialSelection());
              this.updateVariantInputs('', { silent: true });
              this.applyIncompleteSizeAvailability();
              this.exitColourMediaLoading();
              this.signalColourMediaReady();
            } else {
              this.setUnavailable();
              this.exitColourMediaLoading();
              this.signalColourMediaReady();
            }
            return;
          }

          if (!isSizeSelection) {
            await this.updateMedia(html, variant?.featured_media?.id, requestId);
          } else {
            this.signalColourMediaReady();
          }

          if (requestId !== colourMediaRequestId) return;

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          // Size not chosen: Sold out only if selected colour has zero available sizes.
          // Do not treat Shopify's default colour+first-size (often sold-out S) as product sold out.
          if (needsSize) {
            this.applyIncompleteSizeAvailability();
          } else {
            this.productForm?.toggleSubmitButton(
              html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
              window.variantStrings.soldOut
            );
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId, { silent = false } = {}) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          if (!silent) input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      async updateMedia(html, variantFeaturedMediaId, requestId = colourMediaRequestId) {
        if (!variantFeaturedMediaId) {
          this.exitColourMediaLoading();
          this.signalColourMediaReady();
          return;
        }

        const mediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const mediaWrapper = this.querySelector('.product__media-wrapper');

        // Keep the current image visible while the next colour lead is prepared.
        if (!isMobile) {
          this.enterColourMediaLoading();
        } else {
          this.lockMobileMediaFrame(mediaWrapper);
          mediaWrapper?.classList.remove('variant-loading', 'colour-media-preparing', 'has-colour-media-pin');
          mediaWrapper?.querySelectorAll('.hw-colour-media-pin').forEach((el) => el.remove());
          const liveList = mediaWrapper?.querySelector('.product__media-list');
          if (liveList) liveList.scrollLeft = 0;
        }

        const destinationFeatured =
          html.querySelector(`media-gallery [data-media-id="${mediaId}"]`) ||
          html.querySelector(`media-gallery [data-media-id$="-${variantFeaturedMediaId}"]`);
        await this.preloadMediaElement(destinationFeatured);

        const liveExisting =
          this.querySelector(`media-gallery [data-media-id="${mediaId}"]`) ||
          this.querySelector(`media-gallery [data-media-id$="-${variantFeaturedMediaId}"]`);
        await this.preloadMediaElement(liveExisting);

        if (requestId !== colourMediaRequestId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);
        const viewer = this.querySelector('media-gallery [id^="GalleryViewer"]');

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        if (requestId !== colourMediaRequestId) return;

        // Filter colour groups only after the next lead is decoded so mobile
        // does not briefly show an empty gallery under the pin.
        this.signalColourMediaReady();
        const firstVisible = this.querySelector(
          '.product__media-list > .product__media-item:not(.hide-media)'
        );
        const leadId = firstVisible?.dataset?.mediaId || mediaId;
        window.setColourMediaLeadId?.(leadId);

        let liveFeatured = this.activateFeaturedMediaBeforeReveal(this, leadId);
        await this.preloadMediaElement(liveFeatured);

        if (requestId !== colourMediaRequestId) return;

        liveFeatured = this.activateFeaturedMediaBeforeReveal(this, leadId) || liveFeatured;

        const modalThumbs = this.productModal?.querySelector('.rl-modal__thumbnails');
        const modalStage = this.productModal?.querySelector('.rl-modal__stage-track');
        const newModalThumbs = html.querySelector('.rl-modal__thumbnails');
        const newModalStage = html.querySelector('.rl-modal__stage-track');

        if (modalThumbs && newModalThumbs) modalThumbs.innerHTML = newModalThumbs.innerHTML;
        if (modalStage && newModalStage) modalStage.innerHTML = newModalStage.innerHTML;

        this.productModal?.refreshRLModal?.();

        await this.revealColourGalleryLeadOnly(mediaWrapper || this.querySelector('.product__media-wrapper'));
        mediaWrapper?.classList.remove('colour-media-preparing', 'variant-loading', 'has-colour-media-pin');
        mediaWrapper?.querySelectorAll('.hw-colour-media-pin').forEach((el) => el.remove());
        window.clearHwColourMediaFrame?.();
        if (viewer) viewer.style.removeProperty('min-height');
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
